/**
 * Archive (archived cards of a board) and trash (deleted columns/cards, kept
 * 7 days) server state. Restoring from the archive is `useSetCardArchived(false)`.
 */
import {
  keepPreviousData,
  type QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { api, ApiError, queryKeys } from '@/shared/api';

import { invalidateBoard } from './api';
import type { Card, DeletedBoard, TrashItem } from './types';

/** Fetched only while the dialog is open (`enabled`). */
export function useBoardArchive(boardId: string, enabled: boolean, q = '') {
  const search = q.trim();
  return useQuery({
    // under the archive key: restoring / deleting a card refreshes every search of it
    queryKey: [...queryKeys.boards.archive(boardId), search],
    // the server searches the WHOLE archive the viewer may see, not what happens to be loaded
    queryFn: ({ signal }) =>
      api.get<Card[]>(`/v1/boards/${boardId}/archive`, {
        signal,
        ...(search && { query: { q: search } }),
      }),
    enabled,
    placeholderData: keepPreviousData,
  });
}

export function useBoardTrash(boardId: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.boards.trash(boardId),
    queryFn: ({ signal }) => api.get<TrashItem[]>(`/v1/boards/${boardId}/trash`, { signal }),
    enabled,
  });
}

/** A column returns together with the cards deleted with it. */
export function useRestoreTrashItem(boardId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (operationId: string) =>
      api.post(`/v1/boards/${boardId}/trash/${operationId}/restore`),
    onSettled: () => invalidateBoard(client, boardId),
  });
}

/**
 * Undo of a just-made delete: finds the trash entry of the column/card and restores it through
 * the normal trash restore (so a column returns together with its cards, as one group).
 */
export function useUndoTrash(boardId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (entityId: string) => {
      const items = await api.get<TrashItem[]>(`/v1/boards/${boardId}/trash`);
      const item = items.find((candidate) => candidate.entity_id === entityId);
      if (!item) throw new ApiError(404, 'TRASH_ITEM_NOT_FOUND', 'Not in the trash');
      await api.post(`/v1/boards/${boardId}/trash/${item.operation_id}/restore`);
    },
    onSettled: () => invalidateBoard(client, boardId),
  });
}

/** Empties the board's trash in one request. Irreversible; the archive is not affected. */
export function usePurgeAllTrash(boardId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete(`/v1/boards/${boardId}/trash`),
    onSettled: () => invalidateBoard(client, boardId),
  });
}

/** Irreversible. */
export function usePurgeTrashItem(boardId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (operationId: string) => api.delete(`/v1/boards/${boardId}/trash/${operationId}`),
    onSettled: () => invalidateBoard(client, boardId),
  });
}

/** Boards deleted from the workspace (owners only), fetched while the dialog is open. */
export function useDeletedBoards(workspaceId: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.boards.deleted(workspaceId),
    queryFn: ({ signal }) =>
      api.get<DeletedBoard[]>(`/v1/workspaces/${workspaceId}/trash/boards`, { signal }),
    enabled,
  });
}

function settleDeletedBoards(client: QueryClient, workspaceId: string, boardId: string) {
  return Promise.all([
    client.invalidateQueries({ queryKey: queryKeys.boards.deleted(workspaceId) }),
    client.invalidateQueries({ queryKey: queryKeys.boards.list(workspaceId) }),
    client.invalidateQueries({ queryKey: queryKeys.calendar.all }),
    client.invalidateQueries({ queryKey: queryKeys.boards.detail(boardId) }),
  ]).then(() => undefined);
}

/** The board returns; columns and cards deleted before it stay in its own Trash. */
export function useRestoreDeletedBoard(workspaceId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (boardId: string) =>
      api.post(`/v1/workspaces/${workspaceId}/trash/boards/${boardId}/restore`),
    onSettled: (_data, _error, boardId) => settleDeletedBoards(client, workspaceId, boardId),
  });
}

/** Irreversible. */
export function usePurgeDeletedBoard(workspaceId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (boardId: string) =>
      api.delete(`/v1/workspaces/${workspaceId}/trash/boards/${boardId}`),
    onSettled: (_data, _error, boardId) => {
      client.removeQueries({ queryKey: queryKeys.boards.detail(boardId) });
      return settleDeletedBoards(client, workspaceId, boardId);
    },
  });
}
